import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FaSpinner } from "react-icons/fa";
import { toast } from "react-toastify";
import ActionDialog from "../../components/actionDialog";
import { BtnComponent } from "../../components/button";
import { PageDialogContent, PageDialogFooter } from "../../components/dialog";
import { CheckBox } from "../../components/reactHookFormFields/checkbox";
import { useGetChatGroupLocationsQuery, useSyncCheckinCheckoutMutation, useNotifyUserSettingMutation, useUpdateCallSettingMutation } from "../../services/appointmentService";
import { useCheckInCheckOutNurseMutation, useGetNurseLocationsQuery } from "../../services/location_services";
import { locationsParams } from "../settings/SettingsPage/SettingsContent/Locations/locations_constants";
import baseApiHelper from "../../services/baseApiHelper";
import { isPatientChatMessage } from "../../helpers/utility";
import { useGroupChatContext } from "../../context/groupchatContext";
import { CalendarDaysIcon } from "@heroicons/react/20/solid";
import NotificationManagementDialogue from "../settings/SettingsPage/SettingsContent/Locations/CareCoSettingsDialogue/NotificationManagementDialogue";
import DualListBoxLocations from "./DualListBoxLocations";

const LocationCheckin = ({ closeLocationApptPopup = () => {}, fromWhere = "", currentUser = null, currentLocation = null, refetchData = () => {} }) => {
  const [locationChatProcessingId, setLocationChatProcessingId] = useState("");
  const [confirmation, setConfirmation] = useState({ open: false });
  const isMountedRef = useRef(true);

  const groupChatContext = useGroupChatContext();
  const { authData, selectedChat, setSelectedChat } = useMemo(() => ({
    authData: groupChatContext.authData,
    selectedChat: groupChatContext.selectedChat,
    setSelectedChat: groupChatContext.setSelectedChat,
  }), [groupChatContext.authData, groupChatContext.selectedChat, groupChatContext.setSelectedChat]);

  const [
    showNotificationsManagementDialogue,
    setShowNotificationsManagementDialogue,
  ] = useState({
    show: false,
    user_data: null,
  });

  const { data: chatLocation, isLoading, isFetching, refetch: refetchProviderLocations } = useGetChatGroupLocationsQuery(currentUser?.user_id ? { user_id: currentUser?.user_id, include_groups: 1 } : {include_groups: 1 }, { refetchOnMountOrArgChange: true, skip: fromWhere === "nurses_listing" });
  const { data: nurseLocations, isLoading: nurseLocationsLoading, isFetching: nurseLocationsFetching, refetch: refetchNurseLocations } = useGetNurseLocationsQuery({nurse_id: currentUser?.user_id, user_id: currentUser?.user_id, include_groups: 1}, { refetchOnMountOrArgChange: true, skip: fromWhere !== "nurses_listing" });

  const chatLocationList = useMemo(() => {
    return fromWhere === "nurses_listing" ?  nurseLocations?.data || [] :chatLocation || [];
  }, [fromWhere, nurseLocations?.data, chatLocation]);


  const isLoadingList = useMemo(() => {
    return fromWhere === "nurses_listing" ? nurseLocationsLoading : isLoading ;
  }, [fromWhere, nurseLocationsLoading, isLoading]);

  const isFetchingList = useMemo(() => {
    return fromWhere === "nurses_listing" ? nurseLocationsFetching : isFetching;
  }, [fromWhere, nurseLocationsFetching, isFetching]);


  const [notifyUserSetting] = useNotifyUserSettingMutation();
  const [updateCallSetting] = useUpdateCallSettingMutation();
  const [syncCheckinCheckout] = useSyncCheckinCheckoutMutation();
  const [checkInCheckOutNurse] = useCheckInCheckOutNurseMutation();

  const { control, setValue, getValues, reset, formState: { errors } } = useForm({ defaultValue: { chatLocations: [] } });
  const receivePhoneCallVal = useWatch({
    control: control,
    name: "receive_phone_call",
  });

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (chatLocationList?.length) {
      reset({ chatLocations: chatLocationList });
    }
  }, [chatLocationList]);

  const handleSyncCheckinCheckout = async ({allCheckedInSelections, previousCheckedInSelections}) => {
    setLocationChatProcessingId("syncing");

    const checkedInGroups = allCheckedInSelections.filter((selection) => selection.is_group);
    const directLocationIds = allCheckedInSelections
      .filter((selection) => !selection.is_group)
      .map((selection) => selection.id)
      .filter((id) => id != null && id !== "");

    const locationIdsFromGroups = checkedInGroups.flatMap((group) =>
      (Array.isArray(group?.locations) ? group.locations : [])
        .map((loc) => loc?.id)
        .filter((id) => id != null && id !== "")
    );

    const previousDirectIdSet = new Set(
      (previousCheckedInSelections || [])
        .filter((item) => !item?.is_group)
        .map((item) => item?.id)
        .filter((id) => id != null && id !== "")
        .map((id) => String(id))
    );
    const currentDirectIdSet = new Set(directLocationIds.map((id) => String(id)));
    const noLongerDirectIdSet = new Set(
      [...previousDirectIdSet].filter((key) => !currentDirectIdSet.has(key))
    );

    let mergedLocationIds = [...directLocationIds, ...locationIdsFromGroups].filter(
      (id) => !noLongerDirectIdSet.has(String(id))
    );
    let uniqueLocationIds = [...new Map(mergedLocationIds.map((id) => [String(id), id])).values()];
    
    const previousGroups = (previousCheckedInSelections || []).filter((item) => item?.is_group);
    const currentGroupIdSet = new Set(
      (checkedInGroups || []).map((item) => String(item.id))
    );
    const removedGroupMemberIdSet = new Set(
      previousGroups
        .filter((group) => !currentGroupIdSet.has(String(group.id)))
        .flatMap((group) =>
          (Array.isArray(group?.locations) ? group.locations : []).map((loc) => loc?.id).filter(Boolean)
        )
        .map((id) => String(id))
    );
    if (removedGroupMemberIdSet.size > 0) {
      uniqueLocationIds = uniqueLocationIds.filter((id) => !removedGroupMemberIdSet.has(String(id)));
    }

    let payload = { location_ids: uniqueLocationIds };

    if (currentUser?.user_id) {
      payload[fromWhere === "nurses_listing" ? "nurse_id" : "user_id"] = currentUser?.user_id;
    }

    try {
      const apiCall = fromWhere === "nurses_listing" ? checkInCheckOutNurse : syncCheckinCheckout;
      const response = await apiCall(payload);

      if (!isMountedRef.current) {
        return;
      }

      const code = response?.data?.code ?? response?.code;
      if (code === 0) {
        toast.success(response?.data?.msg || response?.msg || "Locations synced successfully");
        if (fromWhere === "nurses_listing") {
          refetchNurseLocations();
        } else {
          refetchProviderLocations();
        }
        refetchData();
        baseApiHelper().invalidateTags([
          "PracticeLocation",
          "NursingHomeLocation",
          "RecentChats",
          "ChatGroupLocations"
        ]);
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      toast.error("Failed to sync locations: " + error?.message);
    } finally {
      if (isMountedRef.current) {
        setLocationChatProcessingId("");
        setSelectedChat({});
      }
    }
  };

  const onChangeNotifySetting = (item, val, index) => {
    let payload = {
      id: item.id,
      notify_via_sms: true,
    };

    let cancleCallBackFun = () => setValue(`chatLocations[${index}].notify_via_sms`, false);
    let confirmationTitle = `Are you sure you want to receive urgent message alerts of <b>${item?.name}</b>.`;
    if (!val) {
      payload.notify_via_sms = false;
      cancleCallBackFun = () => setValue(`chatLocations[${index}].notify_via_sms`, true);
      confirmationTitle = `Are you sure you want to stop receiving urgent message alerts for ${item?.name}?`;
    }

    setConfirmation({
      open: true,
      title: confirmationTitle,
      onCancleCallBackFun: cancleCallBackFun,
      callBackFun: () => onConfirmNotifySetting(payload),
    });
  };

  const onChangeCallSetting = (item) => {
    let payload = {
      location_id: item.id,
      flag: item.model,
      value: item.val ? "allow" : "not_allow",
    };

    const isOtherCallOptionChecked = getValues(`chatLocations[${item.index}].${item.otherOptModel}`);
    let cancleCallBackFun = () => {
      setValue(`chatLocations[${item.index}].${item.model}`, false);
      if (isOtherCallOptionChecked) {
        setValue(`chatLocations[${item.index}].${item.otherOptModel}`, true);
      }
    };
    let confirmationTitle = `Are you sure you want to receive call of <b>${item?.name}</b>.`;
    if (!item.val) {
      payload[item.model] = false;
      cancleCallBackFun = () => {
        setValue(`chatLocations[${item.index}].${item.model}`, true);
        if (isOtherCallOptionChecked) {
          setValue(`chatLocations[${item.index}].${item.otherOptModel}`, true);
        }
      };
      confirmationTitle = `Are you sure you want to stop receiving call for ${item?.name}?`;
    } else {
      setValue(`chatLocations[${item.index}].${item.otherOptModel}`, false);
    }

    setConfirmation({
      open: true,
      title: confirmationTitle,
      onCancleCallBackFun: cancleCallBackFun,
      callBackFun: () => onConfirmCallSetting(payload),
    });
  };

  const onConfirmNotifySetting = async (payload) => {
    const response = await notifyUserSetting(payload);
    if (response.data?.code == 0) {
      toast.success(response.data?.msg);
    }
  };

  const onConfirmCallSetting = async (payload) => {
    const response = await updateCallSetting(payload);
    if (response.data?.code == 0) {
      toast.success(response.data?.msg);
    }
  };

  // const statusChangeApiCall = (ApiFun, item) => {
  //   setLocationChatProcessingId(item.id);

  //   ApiFun({ location_id: item.id }).then((res) => {
  //     toast.success(res.data.msg);

  //     if (getValues("receive_urgent_msgs")) {
  //       onConfirmNotifySetting({ id: item.id, notify_via_sms: true });
  //     }
  //     if (getValues("receive_phone_call")) {
  //       updateCallSetting({
  //         location_id: item.id,
  //         value: "allow",
  //         flag: "call_phone",
  //       });
  //     }
  //     if (getValues("receive_in_app_call")) {
  //       updateCallSetting({
  //         location_id: item.id,
  //         value: "allow",
  //         flag: "call_in_app",
  //       });
  //     }

  //     // closeLocationApptPopup();
  //     setLocationChatProcessingId("");
  //     baseApiHelper().invalidateTags([
  //       "PracticeLocation",
  //       "NursingHomeLocation",
  //       "RecentChats",
  //       "ChatGroupLocations",
  //     ]);
  //     if (!isPatientChatMessage(selectedChat) && item.checked_in) {
  //       baseApiHelper().invalidateTags(["providerLocationChats"]);
  //       if (selectedChat?.location_id == item?.id) {
  //         setSelectedChat({});
  //       }
  //     }
  //   });
  // };

  const unSelectOtherCallOption = (e, unselectKey) => {
    if (e.target.checked) {
      setValue(unselectKey, false);
    }
  };

  return (
    <>
      {showNotificationsManagementDialogue?.show ? (
        <NotificationManagementDialogue 
          setShowNotificationsManagementDialogue={setShowNotificationsManagementDialogue}
          showNotificationsManagementDialogue={showNotificationsManagementDialogue || {}}
          path="checkin"
          locationsData={chatLocationList || []}
          currentUser={currentUser}
          currentLocation={currentLocation}
        />
      ) : null}
      <PageDialogContent
        contentCls="pt-2 px-5"
        hideOverFlow={true}
        contentScroll={false}
      >
        <div className="w-full text-gray-500 min-h-32">
          {isFetchingList ? (
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto absolute inset-0 outline-none focus:outline-none before:absolute before:flex before:w-full before:h-full z-[999]">
              <FaSpinner
                className="animate-spin text-green-300-500 ml-1"
                size={40}
              />
            </div>
          ) : null}
          {(authData?.is_practice_admin || fromWhere != "nurses_listing") ? (
            <div className="flex items-center justify-end py-1">
              <span
                  title="Notification Management"
                  className="cursor-pointer flex items-center gap-2"
                  onClick={() => setShowNotificationsManagementDialogue({ show: true, path: "checkin" })}
                  onKeyDown={(e) => e.key === "Enter" && setShowNotificationsManagementDialogue({ show: true, path: "checkin" })}
              >
                  <p className="text-sm">Notification Management</p>
                  <CalendarDaysIcon className="h-4 w-4 text-[#00acc1] cursor-pointer" />
              </span>
            </div>
          ) : null}

          <DualListBoxLocations
            filteredLocations={chatLocationList}
            locationChatProcessingId={locationChatProcessingId}
            onSyncCheckinCheckout={handleSyncCheckinCheckout}
            isFetching={isFetchingList}
            control={control}
            setValue={setValue}
          />
          {!chatLocationList?.length && !isFetchingList ? (
            <div className="container mx-auto">
              <div className="p-2">
                <p className="text-lg">No location found</p>
              </div>
            </div>
          ) : null}
        </div>
      </PageDialogContent>
      <PageDialogFooter>
        <BtnComponent
          text="Close"
          btnType="default"
          onClick={closeLocationApptPopup}
          disabled={locationChatProcessingId === "syncing"}
        />
      </PageDialogFooter>

      <ActionDialog
        onCancelHandler={() => {
          setConfirmation({ open: false });
          if (confirmation.onCancleCallBackFun) {
            confirmation.onCancleCallBackFun();
          }
        }}
        onConfirmHandler={() => {
          setConfirmation({ open: false });
          confirmation.callBackFun();
        }}
        showModal={confirmation.open}
        title={confirmation?.title}
        setShowModal={() => setConfirmation({ open: false })}
        isHideCloseBtn={true}
      >
        {confirmation.selectedItem && !confirmation.selectedItem?.checked_in ? (
          <>
            <div>
              <CheckBox
                title="Receive Urgent SMS"
                name={`receive_urgent_msgs`}
                control={control}
              />
            </div>
            <div className="flex gap-4 mt-2">
              <CheckBox
                title="Receive Call on Phone"
                name={`receive_phone_call`}
                control={control}
                onChange={(e) => unSelectOtherCallOption(e, "receive_in_app_call")}
              />
              <CheckBox
                title="Receive Call on Ethizo"
                name={`receive_in_app_call`}
                control={control}
                onChange={(e) => unSelectOtherCallOption(e, "receive_phone_call")}
              />
            </div>
          </>
        ) : null}
      </ActionDialog>
    </>
  );
};

export default LocationCheckin;
